// src/fatura-takip/src/pages/Raporlar.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer, CartesianGrid, Tooltip, Legend, ReferenceLine,
  XAxis, YAxis, Bar, ComposedChart, Line, BarChart
} from "recharts";

/* ============================== Helpers ============================== */
const TR = new Intl.NumberFormat("tr-TR");
const TR_CURR0 = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 });
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDateTR = (d) => dayjs(d).format("DD.MM.YYYY");

const parseNumber = (x, d = 0) => {
  if (typeof x === "number") return Number.isFinite(x) ? x : d;
  const raw = (x ?? "").toString().trim();
  if (!raw) return d;
  let s = raw;
  if (raw.includes(",")) s = raw.split(".").join("").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : d;
};
const safe = (s, fb = "") => (s == null || s === "" ? fb : s);
const normalizeTR = (s = "") =>
  s.toString().toUpperCase()
    .replaceAll("İ","I").replaceAll("ı","I")
    .replaceAll("Ş","S").replaceAll("ş","S")
    .replaceAll("Ğ","G").replaceAll("ğ","G")
    .replaceAll("Ü","U").replaceAll("ü","U")
    .replaceAll("Ö","O").replaceAll("ö","O")
    .replaceAll("Ç","C").replaceAll("ç","C");

function rowAmount({ fiyat, adet, iskonto, kdv }) {
  const F = parseNumber(fiyat);
  const A = parseNumber(adet, 1);
  const I = parseNumber(iskonto);
  const K = parseNumber(kdv);
  const net = F * A * (1 - I / 100);
  return net * (1 + K / 100);
}

/* =============== categories -> grup eşleme (opsiyonel) =============== */
async function buildCategoryIndex() {
  const idx = [];
  try {
    const catsSnap = await getDocs(collection(db, "categories"));
    catsSnap.docs.forEach((d) => {
      const c = d.data() || {};
      const kategori = (c.kategori || "").trim();
      const altGrup = (c.altGrup || "").trim();
      const grup = (c.grup || kategori || "Bilinmiyor").trim();
      const icerik = Array.isArray(c.icerik) ? c.icerik.map((s) => normalizeTR(String(s || ""))) : [];
      if (kategori || altGrup || icerik.length) idx.push({ kategori, altGrup, grup, icerik });
    });
  } catch {}
  return idx;
}

/* ============================ Veri çekme ============================ */
async function fetchFaturalarWithGroups() {
  const categories = await buildCategoryIndex();
  const snap = await getDocs(collection(db, "faturalar"));
  const rows = [];

  snap.docs.forEach((doc) => {
    const data = doc.data() || {};
    if (data.deleted) return;
    (data.satirlar || []).forEach((s) => {
      if (s?.deleted) return;
      const tarihSrc = s.tarih?.toDate?.() || data.tarih?.toDate?.();
      const yil = tarihSrc?.getFullYear?.();
      const ay = tarihSrc ? Number(dayjs(tarihSrc).format("MM")) : undefined;

      let kategori = safe(s.kategori, "");
      let altGrup = safe(s.altGrup ?? s.altkategori, "");
      let grup = safe(s.grup, "");
      if (!grup) {
        const haystack = normalizeTR(`${s.urunAdi || ""} ${s.aciklama || ""}`);
        const hit = categories.find((c) => c.icerik.some((kw) => haystack.includes(kw)));
        if (hit) {
          if (!kategori) kategori = hit.kategori || kategori;
          if (!altGrup) altGrup = hit.altGrup || altGrup;
          grup = hit.grup || grup;
        }
      }
      if (!grup) grup = kategori || "Bilinmiyor";

      // --- KDV DAHİL (sayfanın diğer kısımları için mevcut alan) ---
      const tutarKDVDahil = rowAmount({ fiyat: s.fiyat, adet: s.adet, iskonto: s.iskonto, kdv: s.kdv });

      // --- NET TUTAR (KDV Hariç) -> SADECE GRUP GRAFİĞİNDE KULLANACAĞIZ ---
      const netTutar = parseNumber(s.fiyat) * parseNumber(s.adet, 1) * (1 - parseNumber(s.iskonto) / 100);

      rows.push({
        docId: doc.id,
        yil, ay, tarih: tarihSrc,
        grup: grup || "Bilinmiyor",
        altGrup: altGrup || "-",
        kategori: kategori || "-",
        plaka: safe(s.plaka, "-"),
        firma: safe(s.firmaAdi ?? data.firmaAdi ?? s.firma, "-"),
        tutar: tutarKDVDahil, // diğer grafik/tablo aynı kalsın
        netTutar,             // >>> grup grafiği bunu kullanır <<<
      });
    });
  });

  return rows.filter((r) => r.yil && r.ay);
}

/* ======================= Ay bazlı/kümülatif setler ======================= */
function monthlyTotalsAllYears(rows, minYear = 2022) {
  const map = {};
  rows.forEach((r) => {
    if (r.yil < minYear) return;
    map[r.yil] = map[r.yil] || {};
    map[r.yil][r.ay] = (map[r.yil][r.ay] || 0) + r.tutar;
  });
  return map;
}
function ytdForYearBySameDay(rows, year) {
  const cut = dayjs().year(year).endOf("day").toDate();
  return rows.reduce((sum, r) => (r.yil === year && r.tarih && r.tarih <= cut ? sum + r.tutar : sum), 0);
}
function monthAmount(rows, year, month) {
  return rows.reduce((sum, r) => (r.yil === year && r.ay === month ? sum + r.tutar : sum), 0);
}

function buildMultiYearMonthDataset(rows) {
  const today = new Date();
  const thisY = today.getFullYear();
  const thisM = Number(dayjs(today).format("MM"));
  const mt = monthlyTotalsAllYears(rows, 2022);

  const years = Object.keys(mt).map(Number).filter((y) => y >= 2022).sort();
  const data = Array.from({ length: 12 }, (_, i) => ({ ay: pad2(i + 1) }));
  const fullTotals = {};

  years.forEach((y) => {
    let cum = 0;
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      const v = mt[y]?.[m] || 0;
      total += v; cum += v;
      data[m - 1][y] = v;                // aylık (KDV dahil)
      data[m - 1][`${y}_cum`] = cum;     // kümülatif (KDV dahil)
    }
    fullTotals[y] = total;
  });

  const prevY = years.includes(thisY - 1) ? thisY - 1 : null;
  const YTD_this = ytdForYearBySameDay(rows, thisY);
  const YTD_prev = prevY ? ytdForYearBySameDay(rows, prevY) : 0;
  const YTD_pct = YTD_prev > 0 ? Math.round((YTD_this / YTD_prev - 1) * 100) : (YTD_this > 0 ? 100 : 0);

  const thisMonth = monthAmount(rows, thisY, thisM);
  const lastMonthPrev = prevY ? monthAmount(rows, prevY, thisM) : 0;
  const M_pct = lastMonthPrev > 0 ? Math.round((thisMonth / lastMonthPrev - 1) * 100) : (thisMonth > 0 ? 100 : 0);

  const forecast = prevY ? Math.round((fullTotals[prevY] || 0) * (1 + YTD_pct / 100)) : YTD_this;

  return { years, data, thisY, prevY, YTD_this, YTD_prev, YTD_pct, thisMonth, lastMonthPrev, M_pct, monthIdx: thisM - 1, fullTotals, forecast };
}

/* ============================ Grup % değişim grafiği ============================ */
// >>> SADECE BU GRAFİK Net Tutar üzerinden çalışır ve tooltip’te yıl bazlı toplamları gösterir
function buildGroupPctChart(rows) {
  const years = Array.from(new Set(rows.map((r) => r.yil))).filter((y) => y >= 2022).sort();
  const latest = years[years.length - 1];
  const prev = years.length >= 2 ? years[years.length - 2] : null;

  const byGr = {};
  rows.forEach((r) => {
    if (r.yil < 2022) return;
    const g = r.grup || "Bilinmiyor";
    byGr[g] = byGr[g] || { latest: 0, prev: 0 };
    if (r.yil === latest) byGr[g].latest += (r.netTutar || 0);
    if (prev && r.yil === prev) byGr[g].prev += (r.netTutar || 0);
  });

  const data = Object.entries(byGr)
    .map(([grup, v]) => {
      const pct = v.prev > 0 ? Math.round((v.latest / v.prev - 1) * 100) : (v.latest > 0 ? 100 : 0);
      return { grup, pct, latestSum: v.latest, prevSum: v.prev };
    })
    .filter((d) => d.grup && d.grup !== "Bilinmiyor")
    .sort((a, b) => b.pct - a.pct);

  return { data, latest, prev };
}

/* ---- Grup grafiği için özel tooltip (2024: ₺…, 2025: ₺…) ---- */
const GroupPctTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const latestYear = GroupPctTooltip.latestYear || "Bu Yıl";
  const prevYear   = GroupPctTooltip.prevYear   || "Geçen Yıl";

  return (
    <div style={{
      background:"#fff", border:"1px solid #e5e7eb", borderRadius:6,
      padding:8, fontSize:12, boxShadow:"0 2px 10px rgba(0,0,0,.06)"
    }}>
      <div style={{fontWeight:600, marginBottom:6}}>{row.grup}</div>
      <div style={{display:"flex", justifyContent:"space-between", gap:12}}>
        <div>{prevYear}:</div>
        <div style={{fontWeight:700}}>{TR_CURR0.format(row.prevSum || 0)}</div>
      </div>
      <div style={{display:"flex", justifyContent:"space-between", gap:12}}>
        <div>{latestYear}:</div>
        <div style={{fontWeight:700}}>{TR_CURR0.format(row.latestSum || 0)}</div>
      </div>
      <div style={{marginTop:6, color:"#475569"}}>
        Değişim: <b>%{TR.format(row.pct || 0)}</b>
      </div>
    </div>
  );
};

/* ============================== Liste / Pivot ============================== */
function aggregateHier(rows, { from, to, by = "grup", minTotal = 50000 }) {
  const keyTop = (r) => (by === "plaka" ? r.plaka || "-" : r.grup || "Bilinmiyor");
  const keyDetail = (r) => `${by === "plaka" ? r.grup : r.plaka}__${r.altGrup}__${r.firma}`;

  const buckets = new Map();
  rows.forEach((r) => {
    if (!r.tarih) return;
    if (r.tarih < from || r.tarih > to) return;
    const top = keyTop(r);
    const detK = keyDetail(r);
    if (!buckets.has(top)) buckets.set(top, { total: 0, details: new Map() });
    const obj = buckets.get(top);
    obj.total += r.tutar; // KDV dahil (sayfanın diğer kısımları aynı kalsın)
    const d = obj.details.get(detK) || { grup: r.grup, plaka: r.plaka, altGrup: r.altGrup, firma: r.firma, toplam: 0 };
    d.toplam += r.tutar;
    obj.details.set(detK, d);
  });

  const tops = Array.from(buckets.entries())
    .filter(([, v]) => v.total >= minTotal)
    .map(([k, v]) => ({
      key: k,
      total: v.total,
      details: Array.from(v.details.values()).sort((a, b) => b.toplam - a.toplam),
    }))
    .sort((a, b) => b.total - a.total);

  return tops;
}

/* =============== Excel’e birleşik hücrelerle aktarım =============== */
function exportExcelHier(tops, mode) {
  const rows = [];
  const merges = [];
  let r = 1;

  tops.forEach((t) => {
    const start = r + 1;
    t.details.forEach((d, idx) => {
      rows.push([
        t.key,
        mode === "grup" ? d.plaka : d.grup,
        d.altGrup,
        d.firma,
        d.toplam,
        idx === 0 ? t.total : "",
      ]);
      r++;
    });
    const end = r;
    if (end > start) {
      merges.push({ s: { r: start, c: 0 }, e: { r: end, c: 0 } });
      merges.push({ s: { r: start, c: 5 }, e: { r: end, c: 5 } });
    }
  });

  const header = [
    mode === "grup" ? "Grup" : "Plaka",
    mode === "grup" ? "Plaka" : "Grup",
    "Alt Grup", "Firma", "Toplam",
    mode === "grup" ? "Grup Toplam" : "Plaka Toplam",
  ];
  const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
  ws["!merges"] = merges;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, mode === "grup" ? "Grup" : "Plaka");
  XLSX.writeFile(wb, `Raporlar_${mode}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
}

/* ============================== Tooltip (AY/YIL ayrışmış) ============================== */
const SplitTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload || !payload.length) return null;
  const isCum = mode === "cum";
  const items = payload.filter((p) => (isCum ? String(p.dataKey).includes("_cum") : !String(p.dataKey).includes("_cum")));
  if (!items.length) return null;
  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:6,padding:8,fontSize:12,boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
      <div style={{fontWeight:600,marginBottom:6}}>{isCum ? "Yıl" : "Ay"}: {label}</div>
      {items.map((p, i) => (
        <div key={i} style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{width:10,height:10,borderRadius:3,background:p.color}}/>
          <span>{p.name || p.dataKey}:</span>
          <span style={{fontWeight:600}}>{TR_CURR0.format(p.value || 0)}</span>
        </div>
      ))}
    </div>
  );
};

/* ================================== View ================================== */
const Raporlar = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tooltip modu: bar üstünde "month", çizgi üstünde "cum"
  const [hoverKind, setHoverKind] = useState("month"); // "month" | "cum"

  const [dateFrom, setDateFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(dayjs().endOf("day").format("YYYY-MM-DD"));
  const [threshold, setThreshold] = useState(50000);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await fetchFaturalarWithGroups();
      setRows(data);
      setLoading(false);
    })();
  }, []);

  const monthMulti = useMemo(() => buildMultiYearMonthDataset(rows), [rows]);
  const groupPct = useMemo(() => buildGroupPctChart(rows), [rows]);

  const small = { fontSize: 11, lineHeight: "1.1rem" };
  const YEAR_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#22c55e"];

  const fromDate = useMemo(() => dayjs(dateFrom).startOf("day").toDate(), [dateFrom]);
  const toDate = useMemo(() => dayjs(dateTo).endOf("day").toDate(), [dateTo]);

  const topsByGroup = useMemo(
    () => aggregateHier(rows, { from: fromDate, to: toDate, by: "grup",  minTotal: Number(threshold) || 0 }),
    [rows, fromDate, toDate, threshold]
  );
  const topsByPlate = useMemo(
    () => aggregateHier(rows, { from: fromDate, to: toDate, by: "plaka", minTotal: Number(threshold) || 0 }),
    [rows, fromDate, toDate, threshold]
  );

  const prevY = monthMulti.prevY;
  const thisY = monthMulti.thisY;
  const leftLabelPrev = `01.01.${prevY} – ${fmtDateTR(dayjs().year(prevY))}`;
  const leftLabelThis = `01.01.${thisY} – ${fmtDateTR(new Date())}`;

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#4338ca", marginBottom: 8 }}>📊 Raporlar</h2>

      {/* ----------------- 1) 2022+ Aylık (bar) + Kümülatif (çizgi) ----------------- */}
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 600, marginBottom: 6 }}>Aylık Harcama (2022+). Çubuklar: Ay tutarı. Çizgiler: Kümülatif.</h3>

        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={monthMulti.data}
            margin={{ top: 10, right: 16, left: 16, bottom: 10 }}
            onMouseLeave={() => setHoverKind("month")}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="ay" />
            <YAxis width={92} tickFormatter={(v) => TR.format(v)} />
            {/* AY / YIL ayrıştırılmış tooltip */}
            <Tooltip content={<SplitTooltip mode={hoverKind} />} />
            <Legend />
            {monthMulti.years.map((y, idx) => (
              <Bar
                key={`bar_${y}`}
                dataKey={String(y)}
                name={`Ay Tutarı ${y}`}
                fill={YEAR_COLORS[idx % YEAR_COLORS.length]}
                opacity={0.6}
                onMouseMove={() => setHoverKind("month")}
              />
            ))}
            {monthMulti.years.map((y, idx) => (
              <Line
                key={`cum_${y}`}
                type="monotone"
                dataKey={`${y}_cum`}
                name={`Kümülatif ${y}`}
                dot={{ r: 2 }}
                strokeWidth={2}
                stroke={YEAR_COLORS[idx % YEAR_COLORS.length]}
                isAnimationActive={false}
                onMouseMove={() => setHoverKind("cum")}
              />
            ))}
            <ReferenceLine x={pad2(monthMulti.monthIdx + 1)} stroke="#94a3b8" strokeDasharray="4 4" />
          </ComposedChart>
        </ResponsiveContainer>

{/* ------- ÖZET kutuları + Ön Görü (tek grid) ------- */}
{!loading && (
  <div style={{ marginTop: 10, ...small }}>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)", // 4 kutu + sağda Ön Görü
        gap: 8,
      }}
    >
      {/* 1) Geçen yıl bugüne kadar */}
      <div style={{ background:"#eef2ff", border:"1px dashed #93c5fd", borderRadius:8, padding:"8px 10px" }}>
        <div style={{ color:"#4b5563" }}>
          {prevY} yılı bugüne kadar <i>({leftLabelPrev})</i>
        </div>
        <div style={{ fontWeight:700 }}>{TR_CURR0.format(monthMulti.YTD_prev)}</div>
      </div>

      {/* 2) Bu yıl bugüne kadar */}
      <div style={{ background:"#eef2ff", border:"1px dashed #93c5fd", borderRadius:8, padding:"8px 10px" }}>
        <div style={{ color:"#4b5563" }}>
          {thisY} yılı bugüne kadar <i>({leftLabelThis})</i>
        </div>
        <div style={{ fontWeight:700 }}>{TR_CURR0.format(monthMulti.YTD_this)}</div>
      </div>

      {/* 3) Yıllık % değişim */}
      <div style={{ background:"#eef2ff", border:"1px dashed #93c5fd", borderRadius:8, padding:"8px 10px" }}>
        Yıllık değişim yüzdesi(geçen yıl)<div style={{ fontWeight:700 }}>%{TR.format(monthMulti.YTD_pct)}</div>
      </div>

      {/* 4) Aylık % değişim */}
      <div style={{ background:"#eef2ff", border:"1px dashed #93c5fd", borderRadius:8, padding:"8px 10px" }}>
        Aylık değişim yüzdesi(geçen yıl)<div style={{ fontWeight:700 }}>%{TR.format(monthMulti.M_pct)}</div>
      </div>

      {/* 5) Ön Görü (sağda) */}
      <div style={{ background:"#eef2ff", border:"1px dashed #93c5fd", borderRadius:8, padding:"8px 10px" }}>
        {thisY} Sonu Ön Görü: <div style={{ fontWeight:700 }}>{TR_CURR0.format(monthMulti.forecast)}</div>
      </div>
    </div>
  </div>
)}

      </div>

      {/* ----------------------- 2) Grup bazlı — % değişim ----------------------- */}
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 600, marginBottom: 6 }}>Grup Bazlı % Değişim (Son 2 Yıl)</h3>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={groupPct.data.slice(0, 20)}
            layout="vertical"
            margin={{ top: 10, right: 16, left: 120, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => `${TR.format(v)}%`} />
            <YAxis type="category" dataKey="grup" width={220} />

            {/* Özel tooltip: yılları bileşene aktar */}
            {(() => {
              GroupPctTooltip.latestYear = groupPct.latest;
              GroupPctTooltip.prevYear = groupPct.prev;
              return <Tooltip content={<GroupPctTooltip />} />;
            })()}

            <Legend />
            <Bar dataKey="pct" name="% Değişim" fill="#0ea5e9" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ----------------------------- 3) Pivot Listeler ----------------------------- */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 8, ...small }}>
        <label>Başlangıç
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 style={{ marginLeft: 6, padding:"4px 6px", border:"1px solid #d1d5db", borderRadius:6 }}/>
        </label>
        <label>Bitiş
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 style={{ marginLeft: 6, padding:"4px 6px", border:"1px solid #d1d5db", borderRadius:6 }}/>
        </label>
        <label>Tutar Eşiği (TL)
          <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                 style={{ marginLeft: 6, padding:"4px 6px", border:"1px solid #d1d5db", borderRadius:6, width:100 }}/>
        </label>
        <button onClick={() => exportExcelHier(topsByGroup, "grup")}
                style={{ padding:"6px 10px", borderRadius:8, background:"#4338ca", color:"#fff", fontWeight:600 }}>
          Grup Excel
        </button>
        <button onClick={() => exportExcelHier(topsByPlate, "plaka")}
                style={{ padding:"6px 10px", borderRadius:8, background:"#4338ca", color:"#fff", fontWeight:600 }}>
          Plaka Excel
        </button>
      </div>

      {/* Grup bazlı tablo */}
      <div style={{ ...small, marginBottom: 14 }}>
        <h4 style={{ fontWeight: 600, margin: "6px 0" }}>Grup Bazlı (≥ {TR_CURR0.format(Number(threshold) || 0)})</h4>
        <div style={{ overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ minWidth: "100%", fontSize: 11 }}>
            <thead style={{ background: "#f8fafc", color: "#374151" }}>
              <tr>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Grup</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Plaka</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Alt Grup</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Firma</th>
                <th style={{ textAlign: "right", padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Toplam</th>
                <th style={{ textAlign: "right", padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Grup Toplam</th>
              </tr>
            </thead>
            <tbody>
              {topsByGroup.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 10, color:"#6b7280" }}>Kayıt bulunamadı</td></tr>
              ) : (
                topsByGroup.map((g, gi) =>
                  g.details.map((d, di) => (
                    <tr key={`${gi}_${di}`} style={{ background: (gi+di)%2 ? "#ffffff" : "#f9fafb" }}>
                      {di===0 && <td rowSpan={g.details.length} style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", verticalAlign:"top" }}>{g.key}</td>}
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.plaka}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.altGrup}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.firma}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", textAlign:"right" }}>{TR_CURR0.format(d.toplam)}</td>
                      {di===0 && <td rowSpan={g.details.length} style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", textAlign:"right", verticalAlign:"top" }}>{TR_CURR0.format(g.total)}</td>}
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plaka bazlı tablo */}
      <div style={{ ...small }}>
        <h4 style={{ fontWeight: 600, margin: "6px 0" }}>Plaka Bazlı (≥ {TR_CURR0.format(Number(threshold) || 0)})</h4>
        <div style={{ overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ minWidth: "100%", fontSize: 11 }}>
            <thead style={{ background: "#f8fafc", color: "#374151" }}>
              <tr>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Plaka</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Grup</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Alt Grup</th>
                <th style={{ textAlign: "left",  padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Firma</th>
                <th style={{ textAlign: "right", padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Toplam</th>
                <th style={{ textAlign: "right", padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>Plaka Toplam</th>
              </tr>
            </thead>
            <tbody>
              {topsByPlate.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 10, color:"#6b7280" }}>Kayıt bulunamadı</td></tr>
              ) : (
                topsByPlate.map((p, pi) =>
                  p.details.map((d, di) => (
                    <tr key={`${pi}_${di}`} style={{ background: (pi+di)%2 ? "#ffffff" : "#f9fafb" }}>
                      {di===0 && <td rowSpan={p.details.length} style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", verticalAlign:"top" }}>{p.key}</td>}
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.grup}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.altGrup}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb" }}>{d.firma}</td>
                      <td style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", textAlign:"right" }}>{TR_CURR0.format(d.toplam)}</td>
                      {di===0 && <td rowSpan={p.details.length} style={{ padding:"6px 8px", borderBottom:"1px solid #e5e7eb", textAlign:"right", verticalAlign:"top" }}>{TR_CURR0.format(p.total)}</td>}
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Raporlar;
